import type { Program } from '@/types/models';

interface HybridSearchMeta {
  hybrid_rank?: unknown;
}

interface ProgramMeta {
  rerank_score?: unknown;
  hybrid_rank?: unknown;
  hybrid_search?: HybridSearchMeta | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function getMeta(program: Program): ProgramMeta | null {
  return isRecord(program.meta) ? (program.meta as ProgramMeta) : null;
}

function getTopLevelHybridRank(program: Program): number | null {
  const maybe = program as unknown as { hybrid_rank?: unknown };
  return toNumber(maybe.hybrid_rank);
}

function getDetailsRelevance(program: Program): number | null {
  if (!isRecord(program.details)) return null;
  return toNumber(program.details.relevance);
}

/**
 * Computes the program "final" rank score for UI display.
 *
 * - If reranking ran, we prefer `meta.rerank_score` (expected 0..1).
 * - Otherwise we fall back to the base hybrid rank from the Supabase RPC
 *   and preserve the existing UI scaling behavior (`* 10`) to keep the same
 *   baseline display.
 */
export function getProgramFinalRankScore(program: Program): number | null {
  const meta = getMeta(program);

  const rerankScore = toNumber(meta?.rerank_score);
  if (rerankScore !== null) return clamp01(rerankScore);

  const rawHybridRank =
    toNumber(meta?.hybrid_rank) ??
    toNumber(meta?.hybrid_search?.hybrid_rank) ??
    getTopLevelHybridRank(program) ??
    getDetailsRelevance(program);

  if (rawHybridRank === null) return null;

  return clamp01(rawHybridRank * 10);
}
